import { Component, ElementRef } from '@angular/core';
import { IHeaderAngularComp } from 'ag-grid-angular';
import { IHeaderParams } from 'ag-grid-community';
import { NgbPopover } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';import {
  DayOfWeek, DayAbbreviations, ORDERED_DAYS, formatWorkingDays, orderedDaysFrom
} from '../working-days.util';

export interface WeekDaysHeaderContext {
  getWorkingDays(weekKey: string): DayOfWeek[];
  setWorkingDays(weekKey: string, days: DayOfWeek[]): void;
  applyWorkingDaysToAll(weekKey: string): void;
  isTableEnabled(): boolean;
  getWeekStartDay(): DayOfWeek;
}

interface DayCheckbox {
  day: DayOfWeek;
  labelKey: string;
  checked: boolean;
}

const DAY_LABEL_KEYS: Record<DayOfWeek, string> = {
  MONDAY: 'plan.start.day.of.week.monday',
  TUESDAY: 'plan.start.day.of.week.tuesday',
  WEDNESDAY: 'plan.start.day.of.week.wednesday',
  THURSDAY: 'plan.start.day.of.week.thursday',
  FRIDAY: 'plan.start.day.of.week.friday',
  SATURDAY: 'plan.start.day.of.week.saturday',
  SUNDAY: 'plan.start.day.of.week.sunday',
};

@Component({
  standalone: false,
  selector: 'wfm-esp-week-days-header',
  templateUrl: './week-days-header.component.html',
  styleUrls: ['./week-days-header.component.css'],
})
export class WeekDaysHeaderComponent implements IHeaderAngularComp {
  params: IHeaderParams;
  weekKey: string;
  weekNumber: number;
  dayCheckboxes: DayCheckbox[] = [];
  private abbreviations: DayAbbreviations;
  // The body-appended popover panel; captured on `shown` so we can manage focus
  // and trap Tab within it (WCAG 2.4.3 Focus Order / 2.1.2 No Keyboard Trap).
  private popoverPanel: HTMLElement | null = null;

  constructor(private translate: TranslateService, private el: ElementRef) {}

  agInit(params: IHeaderParams): void {
    this.params = params;
    this.weekKey = params.column.getColDef().field;
    this.weekNumber = Number(this.weekKey.replace('week', ''));
    this.abbreviations = this.buildAbbreviations();
  }

  refresh(): boolean {
    return false;
  }

  private buildAbbreviations(): DayAbbreviations {
    const initials = {} as Record<DayOfWeek, string>;
    const rangeAbbr = {} as Record<DayOfWeek, string>;
    ORDERED_DAYS.forEach((day) => {
      const key = day.toLowerCase();
      initials[day] = this.translate.instant(`learning.curve.day.initial.${key}`);
      rangeAbbr[day] = this.translate.instant(`learning.curve.day.short.${key}`);
    });
    return { initials, rangeAbbr };
  }

  private get context(): WeekDaysHeaderContext {
    return this.params.context as WeekDaysHeaderContext;
  }

  get label(): string {
    const days = this.context.getWorkingDays(this.weekKey);
    if (days.length === 0) {
      return this.translate.instant('learning.curve.dialog.working.days.none');
    }
    const text = formatWorkingDays(days, this.abbreviations, this.context.getWeekStartDay());
    return this.translate.instant('learning.curve.dialog.working.days.summary', {
      '0': text,
      '1': days.length,
    });
  }

  get disabled(): boolean {
    return !this.context.isTableEnabled();
  }

  buildCheckboxes(): void {
    const current = this.context.getWorkingDays(this.weekKey);
    this.dayCheckboxes = orderedDaysFrom(this.context.getWeekStartDay()).map((day) => ({
      day,
      labelKey: DAY_LABEL_KEYS[day],
      checked: current.includes(day),
    }));
  }

  // Selection is stored in canonical Monday-first order regardless of the
  // popover's display order, so the saved payload and header label are stable.
  private selectedDays(): DayOfWeek[] {
    const checked = new Set(this.dayCheckboxes.filter((c) => c.checked).map((c) => c.day));
    return ORDERED_DAYS.filter((day) => checked.has(day));
  }

  onToggle(item: DayCheckbox): void {
    item.checked = !item.checked;
    this.context.setWorkingDays(this.weekKey, this.selectedDays());
  }

  onApplyToAll(): void {
    this.context.setWorkingDays(this.weekKey, this.selectedDays());
    this.context.applyWorkingDaysToAll(this.weekKey);
  }

  togglePopover(popover: NgbPopover): void {
    if (this.disabled) {
      return;
    }
    if (popover.isOpen()) {
      popover.close();
      return;
    }
    this.buildCheckboxes();
    popover.open();
  }

  // WCAG 2.4.3: when the popover opens, move focus into it (onto the first day
  // checkbox) so keyboard users land inside the dialog instead of Tab skipping
  // to the next grid header (the popover is appended to <body>, away from the
  // trigger in the DOM/tab order).
  onPopoverShown(): void {
    setTimeout(() => {
      this.popoverPanel = document.querySelector('.working-days-popup');
      const first = this.getFocusable()[0];
      first?.focus();
    });
  }

  onPopoverHidden(): void {
    this.popoverPanel = null;
  }

  // Trap Tab within the popover and close on Escape, returning focus to the
  // trigger button (WCAG 2.1.2 No Keyboard Trap / 2.4.3 Focus Order).
  onPopoverKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.returnFocusToTrigger();
      return;
    }
    if (event.key !== 'Tab') {
      return;
    }
    const focusable = this.getFocusable();
    if (focusable.length === 0) {
      return;
    }
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const active = document.activeElement as HTMLElement;
    if (event.shiftKey && active === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  }

  private getFocusable(): HTMLElement[] {
    if (!this.popoverPanel) {
      return [];
    }
    return Array.from(
      this.popoverPanel.querySelectorAll<HTMLElement>(
        'input[type="checkbox"], button, [tabindex]:not([tabindex="-1"])'
      )
    ).filter((elm) => !elm.hasAttribute('disabled'));
  }

  private returnFocusToTrigger(): void {
    const trigger = this.el.nativeElement.querySelector('.week-days-top') as HTMLElement;
    trigger?.focus();
  }
}
